import {connect} from 'react-redux';

import {makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildTeammatePath, buildTeamPath} from '../../../../helpers/modelHelpers';
import {FrameTypesEnum} from '../../../../models/api/apiTypes';
import {
  InboxesFiltersEnum,
  TeammatesMessageFoldersEnum,
  TeamsFoldersEnum,
} from '../../../../models/url/models/inboxesUrlModel';
import {OnboardingEventsEnum, trackEvent, TrackingTypesEnum} from '../../../../services/tracking';
import {saveCompanyFormAction} from '../../../../store/data/company/companyActions';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {updateSelectedSideFrameIdAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {saveUserAction} from '../../../../store/data/teammates/teammatesActions';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {getFlatTeams} from '../../../../store/data/teams/teamsSelectors';
import {saveUserMetadataAction} from '../../../../store/data/userMetadata/userMetadataActions';
import {getUserMetadata} from '../../../../store/data/userMetadata/userMetadataSelectors';
import {getHasActionCtasBundle} from '../../../../store/featureFlags/featureFlagsSelectors';
import {openDemoInboxLoadingModalAction} from '../../../../store/panels/app/appPanelActions';
import {AppPanelId} from '../../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {WelcomeCarouselFormContainerProps} from '../welcomeCarouselCommon';
import {CompanyFormOptions, WelcomeCarouselCompanyForm} from './welcomeCarouselCompanyForm';

/*
 * Props.
 */

interface WelcomeCarouselCompanyFormContainerProps extends WelcomeCarouselFormContainerProps {
  panelId: AppPanelId;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  user: getUser,
  company: getCompany,
  userMetadata: getUserMetadata,
  teams: getFlatTeams,
  hasActionCtasBundle: getHasActionCtasBundle,
}));

export const WelcomeCarouselCompanyFormContainer = connect(
  mapStateToProps,
  {
    saveUser: saveUserAction,
    saveUserMetadata: saveUserMetadataAction,
    saveCompanyForm: saveCompanyFormAction,
    openDemoInboxLoadingModal: openDemoInboxLoadingModalAction,
    updateSelectedSideFrameId: updateSelectedSideFrameIdAction,
    navigateToLink: navigateToLinkAction,
  },
  (stateProps, dispatchProps, ownProps: WelcomeCarouselCompanyFormContainerProps) => {
    const {user, company, teams, userMetadata, hasActionCtasBundle} = stateProps;
    const {
      saveCompanyForm,
      saveUserMetadata,
      navigateToLink,
      updateSelectedSideFrameId,
      openDemoInboxLoadingModal,
    } = dispatchProps;
    const {panelId, onPreviousClick, onCompleteForm} = ownProps;

    const submitForm = (options: CompanyFormOptions) => {
      const {name, companySize, industry, teamSize} = options;

      // Save the form data.
      saveUserMetadata({industry, teamSize});
      saveCompanyForm({name, companySize});

      // For data consistency with the other onboarding app, we need to track all of three of these events.
      // If the experiment succeeds, we will likely remove most of this tracking.
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.COMPLETED_PERSONNA);
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.COMPLETED_COMPANY_DATA);
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.COMPLETED);

      /* Navigation. */

      // Navigate to display the demo inbox.
      const firstTeam = teams && teams[0];
      if (firstTeam && !hasActionCtasBundle) {
        navigateToLink(buildTeamPath(firstTeam.id, TeamsFoldersEnum.INBOX, InboxesFiltersEnum.OPEN));
      }

      // If we have the personalized demo inbox, we navigate to Sent temporarily while the inbox loads.
      // The demoInboxLoadingModal will take care of navigating the user back to the team workspace.
      if (hasActionCtasBundle) {
        navigateToLink(buildTeammatePath(user.id, TeammatesMessageFoldersEnum.SENT));
        openDemoInboxLoadingModal(panelId);
      }

      // Either way, open the start guide and complete the form.
      updateSelectedSideFrameId(FrameTypesEnum.START_GUIDE);

      // Advance the carousel.
      onCompleteForm();
    };

    return {
      user,
      company,
      industry: userMetadata.industry,
      teamSize: userMetadata.teamSize,
      apolloEnrichmentData: userMetadata.apolloEnrichmentData,
      onPreviousClick,
      submitForm,
    };
  },
)(WelcomeCarouselCompanyForm);
